import React from 'react';
import {
  chipStyle,
  chipWrapStyle,
  rowMetaStyle,
  rowTitleStyle,
} from './dashboardStyles';

const sensitivityLabels = { normal: '일반', caution: '주의', sensitive: '민감', restricted: '제한' };
const sensitivityColors = { normal: '#2563eb', caution: '#d97706', sensitive: '#dc2626', restricted: '#7f1d1d' };
const shadowColors = { ok: '#16a34a', warning: '#d97706', danger: '#dc2626' };
const categoryLabels = {
  core: '기본',
  settings: '설정',
  sales: '영업',
  report: '보고',
  money: '금액',
  finance: '금액',
  hr: '인사',
  platform: '플랫폼',
  dashboard: '상황판',
  portal: '직원 포털',
  internal: '내부',
};

function badgeStyle(color, filled = false) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 6,
    border: `1px solid ${color}`,
    background: filled ? color : 'var(--bg-card)',
    color: filled ? '#fff' : color,
    padding: '2px 7px',
    fontSize: 11,
    fontWeight: 900,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
  };
}

function actionButtonStyle(disabled, tone = 'default') {
  const colors = {
    default: ['var(--border)', 'var(--bg-card)', 'var(--text)'],
    info: ['#2563eb', 'rgba(37,99,235,0.08)', '#2563eb'],
  };
  const [border, background, color] = colors[tone] || colors.default;
  return {
    border: `1px solid ${disabled ? 'var(--border)' : border}`,
    background: disabled ? 'var(--bg-subtle)' : background,
    color: disabled ? 'var(--text-muted)' : color,
    borderRadius: 6,
    padding: '5px 8px',
    fontSize: 12,
    fontWeight: 850,
    cursor: disabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap',
  };
}

function PermissionChips({ permissions }) {
  if (!permissions?.length) {
    return <div style={rowMetaStyle}>연결된 권한 없음</div>;
  }
  return (
    <div style={chipWrapStyle}>
      {permissions.map((permission) => (
        <span key={permission.code} style={chipStyle} title={permission.code}>
          {permission.label} · {permission.holderCount}명
        </span>
      ))}
    </div>
  );
}

function EmployeeSamples({ employees, count }) {
  if (!count) return <span style={rowMetaStyle}>현재 배정 직원 없음</span>;
  const names = (employees || []).map((user) => user.name).filter(Boolean);
  return (
    <span style={rowMetaStyle}>
      {count}명{names.length ? ` · ${names.join(', ')}` : ''}
      {count > names.length ? ` 외 ${count - names.length}명` : ''}
    </span>
  );
}

export default function TenantModuleTable({
  modules,
  tenant,
  savingKey,
  previewLoadingKey,
  onToggle,
  onPreview,
}) {
  return (
    <div style={{ marginTop: 10, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1060 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-sub)', fontSize: 11 }}>
            {['모듈', '상태', 'Shadow', '민감도', '배정 직원', '연결 권한'].map((label) => (
              <th key={label} style={{ textAlign: 'left', padding: '7px 6px' }}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(modules || []).map((module) => {
            const color = sensitivityColors[module.sensitivity] || sensitivityColors.normal;
            const key = `${tenant.code}:${module.code}`;
            const busy = Boolean(savingKey) || Boolean(previewLoadingKey);
            return (
              <tr key={module.code} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '9px 6px', verticalAlign: 'top' }}>
                  <div style={rowTitleStyle}>{module.name}</div>
                  <div style={rowMetaStyle}>
                    {module.code} · {categoryLabels[module.category] || module.category}
                  </div>
                  {module.description && (
                    <div style={{ ...rowMetaStyle, marginTop: 3 }}>{module.description}</div>
                  )}
                </td>
                <td style={{ padding: '9px 6px', verticalAlign: 'top' }}>
                  <span style={badgeStyle(module.isEnabled ? '#16a34a' : '#64748b', module.isEnabled)}>
                    {module.isEnabled ? '사용 중' : '꺼짐'}
                  </span>
                  {module.note && <div style={{ ...rowMetaStyle, marginTop: 5 }}>{module.note}</div>}
                  <div style={{ ...rowMetaStyle, marginTop: 5 }}>직원 권한 자동변경 없음</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
                    <button
                      type="button"
                      style={actionButtonStyle(busy)}
                      disabled={busy}
                      onClick={() => onToggle(tenant, module)}
                    >
                      {savingKey === key ? '저장 중' : module.isEnabled ? 'SaaS 끄기' : 'SaaS 켜기'}
                    </button>
                    <button
                      type="button"
                      style={actionButtonStyle(busy, 'info')}
                      disabled={busy}
                      onClick={() => onPreview(tenant, module)}
                    >
                      {previewLoadingKey === key ? '확인 중' : '권한 미리보기'}
                    </button>
                  </div>
                </td>
                <td style={{ padding: '9px 6px', verticalAlign: 'top', minWidth: 150 }}>
                  <span style={badgeStyle(shadowColors[module.shadowCheck?.severity] || '#64748b')}>
                    {module.shadowCheck?.label || '검증 필요'}
                  </span>
                  <div style={{ ...rowMetaStyle, marginTop: 5, lineHeight: 1.35 }}>
                    {module.shadowCheck?.detail || '현재 원장과 권한 노출을 비교하지 못했습니다.'}
                  </div>
                  {module.shadowCheck?.recommendation && module.shadowCheck.type !== 'aligned' && (
                    <div style={{ ...rowMetaStyle, marginTop: 6, lineHeight: 1.35 }}>
                      처리: {module.shadowCheck.recommendation.actionLabel}
                    </div>
                  )}
                </td>
                <td style={{ padding: '9px 6px', verticalAlign: 'top' }}>
                  <span style={badgeStyle(color)}>{sensitivityLabels[module.sensitivity] || module.sensitivity}</span>
                </td>
                <td style={{ padding: '9px 6px', verticalAlign: 'top', minWidth: 170 }}>
                  <EmployeeSamples employees={module.affectedEmployees} count={module.assignedUserCount} />
                </td>
                <td style={{ padding: '9px 6px', verticalAlign: 'top' }}>
                  <PermissionChips permissions={module.permissions} />
                </td>
              </tr>
            );
          })}
          {(!modules || modules.length === 0) && (
            <tr>
              <td colSpan={6} style={{ padding: 16, color: 'var(--text-sub)', textAlign: 'center', fontSize: 13 }}>
                조건에 맞는 모듈이 없습니다.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
